import { gql, useLazyQuery } from '@apollo/client'
import { Box, Card } from '@mui/material'
import Image from 'next/image'
import { useCallback, useMemo } from 'react'
import type { GetHomeScreenQuery, GetVideoItemCardTrailerQuery } from '~/__generated__/graphql'
import { DurationLabel } from '~/components/DurationLabel'
import { UpComingChip } from '~/features/play_list/components/chips/UpComingChip'
import { useNewVideoItemCardActions } from '~/features/video/hooks/useNewVideoItemCardActions'
import { useMediaQuery } from '~/hooks/useMediaQuery'
import { thumbnailPlaceholder } from '~/libs/constants'
import { Colors } from '~/libs/mui/color'
import { getVideoWatchedPercent } from '~/libs/video/getVideoWatchedPercent'
import { HoverOverlayCard } from './HoverOverlayCard'
import { getSizeStyle } from './getSizeStyle'

const GET_VIDEO_ITEM_CARD_TRAILER_QUERY = gql`
  query GetVideoItemCardTrailer($id: ID!) {
    originalVideo(id: $id) {
      id
      trailer {
        id
        fileUrl
        vtts {
          default
          label
          locale
          url
        }
      }
    }
  }
`

export type NewVideoItemCardProps = {
  video: NonNullable<GetHomeScreenQuery['homeScreens'][0]['videos']>[0]
  onGetConversion?: () => void
}

export const NewVideoItemCard = ({ video, onGetConversion }: NewVideoItemCardProps) => {
  const { isSm } = useMediaQuery()
  const sizeStyle = getSizeStyle(false)
  const isUpComing = video.status === 'upcoming'

  const watchedPercent = useMemo(() => {
    if (!video.viewingTime) return undefined
    return getVideoWatchedPercent(
      { minutes: video.duration?.minutes ?? 0, seconds: video.duration?.seconds ?? 0 },
      { minutes: video.viewingTime.viewingMin ?? 0, seconds: video.viewingTime.viewingSecond ?? 0 },
    )
  }, [video])

  const {
    onClickVideoDetail,
    handlePointerEnter,
    handlePointerLeave,
    showHoverLayer,
    hoverStatus,
    cardRect,
    cardRef,
  } = useNewVideoItemCardActions(onGetConversion)

  const [fetchVideoData, { data: videoData }] = useLazyQuery<GetVideoItemCardTrailerQuery>(
    GET_VIDEO_ITEM_CARD_TRAILER_QUERY,
    { variables: { id: video.id } },
  )

  const handlePointerEnterWithPrefetch = useCallback(() => {
    fetchVideoData()
    handlePointerEnter()
  }, [fetchVideoData, handlePointerEnter])

  return (
    <>
      <Box
        position='relative'
        onPointerEnter={handlePointerEnterWithPrefetch}
        onPointerLeave={handlePointerLeave}
        ref={cardRef}
      >
        <Card
          sx={{
            position: 'relative',
            cursor: 'pointer',
            ...sizeStyle,
          }}
          onClick={() => {
            onGetConversion?.()
            onClickVideoDetail(Number(video.id))
          }}
        >
          <Image
            alt='thumbnail'
            src={isSm ? video.landscapeThumbnail ?? '' : video.portraitThumbnail ?? ''}
            fill
            sizes='25vw'
            style={{
              objectFit: 'cover',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
            placeholder='blur'
            blurDataURL={thumbnailPlaceholder}
          />
          {isUpComing && <UpComingChip />}
          <Box position='absolute' top={2} right={2}>
            <DurationLabel
              durationMinute={video.duration?.minutes ?? 0}
              durationSecond={video.duration?.seconds ?? 0}
            />
          </Box>
          {!!watchedPercent && (
            <Box
              position='absolute'
              bottom={0}
              left={0}
              height='3px'
              width={`${watchedPercent * 100}%`}
              sx={{ backgroundColor: Colors.red, borderRadius: '1px' }}
            />
          )}
        </Card>
      </Box>
      {showHoverLayer && (
        <HoverOverlayCard
          video={video}
          cardRect={cardRect}
          hoverState={hoverStatus}
          onHoverEnter={handlePointerEnterWithPrefetch}
          onHoverLeave={handlePointerLeave}
          trailer={videoData?.originalVideo?.trailer}
        />
      )}
    </>
  )
}
